/**
 * Guard against infinite recursion in HTMLMediaElement.canPlayType.
 *
 * Some third-party scripts (in-app browser WebViews, browser extensions) override
 * canPlayType with implementations that call this.canPlayType() recursively,
 * causing "RangeError: Maximum call stack size exceeded" on iOS Safari.
 *
 * This guard keeps a stable wrapper installed on the prototype so later overrides
 * still route through the recursion detector.
 */
type CanPlayTypeFn = (this: HTMLMediaElement, type: string) => CanPlayTypeResult;

const canPlayTypeGuardMarker = Symbol.for('universalx.canPlayTypeGuard');
const recursiveCanPlayTypePattern = /\bthis(?:\.canPlayType|\[['"]canPlayType['"]\])/;

const isSafeFallback = (fn: CanPlayTypeFn) => !recursiveCanPlayTypePattern.test(Function.prototype.toString.call(fn));

if (typeof window !== 'undefined' && typeof HTMLMediaElement !== 'undefined') {
  const prototype = HTMLMediaElement.prototype as typeof HTMLMediaElement.prototype & Record<PropertyKey, unknown>;

  if (!prototype[canPlayTypeGuardMarker]) {
    const descriptor = Object.getOwnPropertyDescriptor(prototype, 'canPlayType');
    const initialCanPlayType = descriptor?.value;

    if (descriptor && typeof initialCanPlayType === 'function') {
      let currentCanPlayType = initialCanPlayType as CanPlayTypeFn;
      let safeFallbackCanPlayType: CanPlayTypeFn | null = isSafeFallback(currentCanPlayType)
        ? currentCanPlayType
        : null;
      const activeElements = new WeakSet<HTMLMediaElement>();

      const fallbackCanPlayType = (element: HTMLMediaElement, type: string): CanPlayTypeResult => {
        if (!safeFallbackCanPlayType) {
          return '';
        }

        try {
          return safeFallbackCanPlayType.call(element, type);
        } catch {
          return '';
        }
      };

      const guardedCanPlayType: CanPlayTypeFn = function (type: string): CanPlayTypeResult {
        if (activeElements.has(this)) {
          return fallbackCanPlayType(this, type);
        }

        activeElements.add(this);

        try {
          return currentCanPlayType.call(this, type);
        } catch (error) {
          if (error instanceof RangeError) {
            return fallbackCanPlayType(this, type);
          }

          throw error;
        } finally {
          activeElements.delete(this);
        }
      };

      // Preserve the wrapper even when third-party scripts assign a new implementation later.
      Object.defineProperty(prototype, 'canPlayType', {
        configurable: descriptor.configurable,
        enumerable: descriptor.enumerable,
        get() {
          return guardedCanPlayType;
        },
        set(value: CanPlayTypeFn) {
          if (typeof value !== 'function' || value === guardedCanPlayType) {
            return;
          }

          currentCanPlayType = value;

          if (isSafeFallback(value)) {
            safeFallbackCanPlayType = value;
          }
        },
      });

      Object.defineProperty(prototype, canPlayTypeGuardMarker, {
        value: true,
        configurable: true,
      });
    }
  }
}
